import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../App';
import { Star, Check, Zap, AlertCircle, Loader2, X } from 'lucide-react';

export default function PawsSubscriptions() {
  const [plans, setPlans] = useState([]);
  const [activeSub, setActiveSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('monthly'); // monthly | annual

  useEffect(() => {
    Promise.all([
      apiFetch('/api/subscriptions/plans'),
      apiFetch('/api/subscriptions/my'),
    ]).then(([plansData, subData]) => {
      setPlans(plansData);
      setActiveSub(subData?.id ? subData : null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSubscribe = async (planId) => {
    setSubscribing(planId);
    setError('');
    try {
      const sub = await apiFetch('/api/subscriptions/subscribe', {
        method: 'POST',
        body: JSON.stringify({ planId, paymentMethod: 'ONLINE' }),
      });
      setActiveSub(sub);
    } catch (e) {
      setError(e.message);
    }
    setSubscribing(null);
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    setError('');
    try {
      await apiFetch('/api/subscriptions/cancel', { method: 'POST' });
      setActiveSub(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const shownPlans = plans.filter(p =>
    tab === 'monthly' ? p.billingCycle === 'MONTHLY' : p.billingCycle === 'ANNUAL'
  );

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <Loader2 size={32} className="animate-spin text-petfolk-orange" />
    </div>
  );

  return (
    <div className="max-w-4xl" data-testid="paws-subscriptions">
      <div className="mb-6">
        <h1 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Subscriptions</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Subscribe & save on regular grooming</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-xl mb-4" style={{ background: 'rgba(239,68,68,0.08)', color: '#EF4444' }}>
          <AlertCircle size={16} />
          <p className="text-sm">{error}</p>
          <button onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {/* Active Subscription Banner */}
      {activeSub && (
        <div className="rounded-2xl p-6 mb-8 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #FF8C42 100%)' }} data-testid="active-sub-card">
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-1">
              <Star size={18} className="text-yellow-300" fill="currentColor" />
              <p className="text-white/80 text-sm font-semibold">Active Subscription</p>
            </div>
            <p className="font-outfit text-2xl font-bold mb-1">{activeSub.planName}</p>
            <p className="text-white/70 text-sm">Renews: {new Date(activeSub.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            <div className="flex items-center gap-4 mt-4">
              {activeSub.sessionsTotal > 0 && (
                <div className="text-center">
                  <p className="font-outfit text-2xl font-bold">{activeSub.sessionsTotal - activeSub.sessionsUsed}</p>
                  <p className="text-white/70 text-xs">Sessions left</p>
                </div>
              )}
              {activeSub.sessionsTotal === -1 && (
                <div className="text-center">
                  <p className="font-outfit text-2xl font-bold">∞</p>
                  <p className="text-white/70 text-xs">Unlimited</p>
                </div>
              )}
            </div>
            <button
              onClick={handleCancel}
              className="mt-4 px-4 py-2 rounded-xl text-sm font-semibold bg-white/20 hover:bg-white/30 transition-all"
            >
              Cancel Subscription
            </button>
          </div>
          <div className="absolute -top-4 -right-4 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -bottom-6 -right-8 w-40 h-40 rounded-full bg-white/5" />
        </div>
      )}

      {/* Billing Cycle Toggle */}
      <div className="flex gap-2 mb-6 p-1 rounded-2xl w-fit" style={{ background: 'var(--bg-secondary)' }}>
        <button
          onClick={() => setTab('monthly')}
          className={`px-6 py-2 rounded-xl text-sm font-semibold transition-all ${tab === 'monthly' ? 'text-white' : ''}`}
          style={tab === 'monthly' ? { background: 'var(--petfolk-orange)' } : { color: 'var(--text-secondary)' }}
        >
          Monthly
        </button>
        <button
          onClick={() => setTab('annual')}
          className={`px-6 py-2 rounded-xl text-sm font-semibold transition-all ${tab === 'annual' ? 'text-white' : ''}`}
          style={tab === 'annual' ? { background: 'var(--petfolk-purple)' } : { color: 'var(--text-secondary)' }}
        >
          Annual <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full" style={{ background: tab === 'annual' ? 'rgba(255,255,255,0.2)' : 'rgba(139,92,246,0.15)', color: tab === 'annual' ? 'white' : '#8B5CF6' }}>Save 2 months</span>
        </button>
      </div>

      {/* Plan Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shownPlans.map(plan => {
          const isActive = activeSub?.planId === plan.id;
          return (
            <div
              key={plan.id}
              className={`petfolk-card p-6 relative transition-all ${plan.popular ? 'ring-2' : ''}`}
              style={plan.popular ? { '--tw-ring-color': plan.color } : {}}
              data-testid={`plan-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white flex items-center gap-1" style={{ background: plan.color }}>
                  <Zap size={10} fill="white" /> Most Popular
                </div>
              )}
              <div className="mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${plan.color}20` }}>
                  <Star size={20} style={{ color: plan.color }} fill={plan.color} />
                </div>
                <h3 className="font-outfit font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{plan.name}</h3>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{plan.tagline}</p>
              </div>
              <div className="mb-4">
                <span className="font-outfit font-extrabold text-3xl" style={{ color: 'var(--text-primary)' }}>₹{plan.price.toLocaleString()}</span>
                <span className="text-sm ml-1" style={{ color: 'var(--text-secondary)' }}>/{plan.billingCycle === 'ANNUAL' ? 'year' : 'mo'}</span>
              </div>
              <ul className="space-y-2 mb-6">
                {plan.features.map(f => (
                  <li key={f} className="flex items-start gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    <Check size={13} className="shrink-0 mt-0.5" style={{ color: plan.color }} />
                    {f}
                  </li>
                ))}
              </ul>
              {isActive ? (
                <div className="w-full text-center py-2.5 rounded-xl text-sm font-bold" style={{ background: `${plan.color}20`, color: plan.color }}>
                  ✓ Current Plan
                </div>
              ) : (
                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={subscribing === plan.id}
                  className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
                  style={{ background: plan.color }}
                  data-testid={`subscribe-${plan.id}`}
                >
                  {subscribing === plan.id ? <><Loader2 size={14} className="animate-spin" /> Processing...</> : activeSub ? 'Switch Plan' : 'Subscribe'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Benefits Section */}
      <div className="mt-8 petfolk-card p-6">
        <h2 className="font-outfit font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Why Subscribe?</h2>
        <div className="grid sm:grid-cols-3 gap-4 text-sm">
          {[
            { emoji: '💰', title: 'Save Money', desc: 'Pay less per session compared to one-time bookings' },
            { emoji: '📅', title: 'Priority Booking', desc: 'Get preferred time slots before non-subscribers' },
            { emoji: '🎯', title: 'Consistent Care', desc: 'Same groomer who knows your pet\'s needs' },
          ].map(b => (
            <div key={b.title} className="p-4 rounded-xl" style={{ background: 'var(--bg-secondary)' }}>
              <span className="text-2xl">{b.emoji}</span>
              <p className="font-semibold mt-2 mb-1" style={{ color: 'var(--text-primary)' }}>{b.title}</p>
              <p style={{ color: 'var(--text-secondary)' }}>{b.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
